import React, {useContext, useEffect, useRef} from "react";
import PillRow, {HeaderPillRow, PillRecord} from "../../components/pills/PillRow";
import './BranchRankingStyles.css'
import styled from "styled-components";
import TabTitle from "../tabTitle/TabTitle";
import EuropeRecord from "../../models/EuropeRecord";
import Branch from "../../interfaces/Branch";
import {GlobalDataContext} from "../../contexts/GlobalDataContext";
import {useTranslation} from "react-i18next";
import LatamRecord from "../../models/LatamRecord";
import {RegionSelectedContext, RegionValue, YearSelectedType} from "../../contexts/RegionSelectedContext";
import TabFooter from "../tabFooter/TabFooter";
import Tab from "../tab/Tab";

interface PillsComponentProps {
  records: PillRecord[],
  year: YearSelectedType,
  currency?: string,
  active: boolean,
}

const StyledPillsComponent = styled.div`
  display: flex;
  flex-direction: row;

  @media (max-height: 900px) {
    font-size: 0.7rem;
  }

  .table {
    flex: 10;
    display: flex;
    flex-direction: column;
    min-height: 0;
  }

  /* WIDE DESKTOP (>=1024px): the two rankings sit side by side, fill the
     available height and let only the rows scroll, so the outer tab never
     scrolls and the MAPFRE row can always be revealed (see auto-scroll effect
     below). Below 1024px the rankings stack and the whole tab scrolls. */
  @media (min-width: 1024px) {
    flex: 1;
    min-height: 0;

    .rowsScroll {
      position: relative;
      flex: 1;
      min-height: 0;
      /* Safety cap so the rows always scroll even if the flex height does not
         propagate; .content clips any excess so the outer tab never scrolls. */
      max-height: calc(100vh - 360px);
      overflow-y: auto;
    }
  }
`;

const PillsComponent: React.FC<PillsComponentProps> = ({ records, year, currency, active }) => {
    const rowsRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        // Only the wide (>=1024px) layout has per-table scrolling; below that the
        // rankings stack and the whole tab scrolls, so no auto-scroll is needed.
        if (!active || window.innerWidth < 1024) return;

        const container = rowsRef.current;
        if (!container) return;

        // Defer until layout is available (the tab body may have just become visible).
        const frame = requestAnimationFrame(() => {
            const mapfreRow = container.querySelector<HTMLElement>('.mapfre-row');
            if (!mapfreRow) return;
            container.scrollTop =
                mapfreRow.offsetTop - container.clientHeight / 2 + mapfreRow.clientHeight / 2;
        });

        return () => cancelAnimationFrame(frame);
    }, [records, active]);

    return (
        <StyledPillsComponent>
            <div className={'table'}>
                <HeaderPillRow
                    year={ year }
                    isDark={ true }
                    currency= { currency }
                  />
                <div className={'rowsScroll'} ref={ rowsRef }>
                    { records.map((record, i) => {
                        return (
                          <PillRow
                              number={ i + 1 }
                              record={ record }
                              isDark={ true }
                              key={ record.company_group }
                            />
                        );
                    })}
                </div>
            </div>
        </StyledPillsComponent>
    )
};

const BranchRankingContent = styled.div`
    /* Wide desktop: fill the body exactly so only the rows scroll. */
    @media (min-width: 1024px) {
        display: flex;
        flex-direction: column;
        flex: 1;
        height: 100%;
        min-height: 0;
        box-sizing: border-box;
        padding-top: 20px;
        overflow: hidden;
    }

    /* Narrow desktop / tablet: stack the rankings and let the tab scroll. */
    @media (min-width: 481px) and (max-width: 1023px) {
        display: flex;
        flex-direction: column;
        flex: 1;
        margin-top: 20px;
    }

    @media (max-width: 480px) {
        display: flex;
        flex-direction: column;
        flex: 1;
        margin-top: 31px;
        margin-bottom: 2%;
    }

    @media (max-height: 900px) {
      h3 {
        display: none;
      }
    }
`;

const HorizontalSeparator = styled.div`
  border-bottom: 4px solid white;
  margin-bottom: 20px;
  margin-top: 5px;
  min-width: 50px;
  width: 3%;

  @media (max-height: 900px) {
    display: none;
  }
`;

const parseEuropeRecords = (europeRecords: EuropeRecord[], yearSelected: YearSelectedType): [PillRecord[], PillRecord[]] => {
  const branchRecords = (branch: Branch): PillRecord[] => {
    const sorted = europeRecords
      .filter((record: EuropeRecord) => record.year === yearSelected.year && record.niiff === yearSelected.niiff)
      .filter((record: EuropeRecord) => record.branch === branch)
      .sort((r1: EuropeRecord, r2: EuropeRecord) => r2.premiums_current_year - r1.premiums_current_year);

    return sorted
      .slice(0, sorted.length > 15 ? 20 : 15)
      .map((x: EuropeRecord) => {
        return {
          company_group: x.company_group,
          premiums: x.premiums_current_year,
          registered_office: x.registered_office
        };
      });
  };

  return [branchRecords(Branch.LIFE), branchRecords(Branch.NO_LIFE)];
};

const parseLatamRecords = (latamRecords: LatamRecord[], yearSelected: YearSelectedType, region: RegionValue): [PillRecord[], PillRecord[]] => {
  const lifeRecords: PillRecord[] = latamRecords
    .filter((record: LatamRecord) => record.year === yearSelected.year && record.niiff === yearSelected.niiff)
    .filter((record: LatamRecord) => record.branch === Branch.LIFE)
    .sort((r1: LatamRecord, r2: LatamRecord) => r2.premiums - r1.premiums)
    .slice(0, region === "latam" ? 15 : 20)
    .map((x: LatamRecord) => {
      return {
        company_group: x.company_group,
        premiums: x.premiums,
        registered_office: x.registered_office
      };
    });

  const noLifeRecords: PillRecord[] = latamRecords
    .filter((record: LatamRecord) => record.year === yearSelected.year && record.niiff === yearSelected.niiff)
    .filter((record: LatamRecord) => record.branch === Branch.NO_LIFE)
    .sort((r1: LatamRecord, r2: LatamRecord) => r2.premiums - r1.premiums)
    .slice(0, region === "latam" ? 15 : 20)
    .map((x: LatamRecord) => {
      return {
        company_group: x.company_group,
        premiums: x.premiums,
        registered_office: x.registered_office
      };
    });

  return [lifeRecords, noLifeRecords];
};


const BranchRankingComponent: React.FC<{ isTabSelected: boolean }> = ({isTabSelected}) => {
    const { t } = useTranslation();

    // @ts-ignore
    const { latamRecords, europeRecords, reaseguroRecords } = useContext(GlobalDataContext);
    // @ts-ignore
    const { regionSelected, yearSelected } = useContext(RegionSelectedContext);

    if (Object.keys(europeRecords).length === 0 || Object.keys(latamRecords).length === 0 || Object.keys(reaseguroRecords).length === 0)
      return <></>;

    const [lifeRecords, noLifeRecords] =
      regionSelected === "eu"
        ? parseEuropeRecords(europeRecords, yearSelected)
        : (regionSelected === "latam" 
          ? parseLatamRecords(latamRecords, yearSelected, regionSelected)
          : parseLatamRecords(reaseguroRecords, yearSelected, regionSelected));
    
    const currency = regionSelected === 'eu' ? 'M€' : 'M USD';

    const tabContent = (
      <BranchRankingContent className='content'>
          <h3>{ t('branchRanking.subtitle', { currency }) }</h3>
          <HorizontalSeparator />
          <div className={'branchRankingGraphicsContainer'}>
              <div className={'graphicContainer'}>
                  <div className={'lifeText'}>
                      { t('branchRanking.lifeRankingTitle') }
                  </div>
                  <PillsComponent
                      records={ lifeRecords }
                      year={ yearSelected }
                      currency= { currency }
                      active={ isTabSelected }
                    />
              </div>
              <div className={'graphicContainer'}>
                  <div className={'lifeText'}>
                      { t('branchRanking.noLifeRankingTitle') }
                  </div>
                  <PillsComponent
                      records={ noLifeRecords }
                      year={ yearSelected }
                      currency= { currency }
                      active={ isTabSelected }
                    />
              </div>
          </div>
      </BranchRankingContent>
    );

    return (
      <Tab
        isOpen={ isTabSelected }
        title={
          <TabTitle
              title={ t('branchRanking.title') }
              isTabSelected={isTabSelected}
          />
        }
        body={ tabContent }
        footer={ <TabFooter /> }
      />
    );
};

export default BranchRankingComponent;
